import { useLogHasESignDocuments } from "../../app/Providers/LogOnReadyContext";
import { Alerts as AlertsResponse, getStateName, phoneNumbers } from "shared";
import { AlertLevel } from "../Alert";
import { UseESign } from "./useESign";
import { useUrls } from "../../app/Providers/AppContext";
import { useFetchApi } from "../../hooks/useFetchApi";

type ButtonAction = {
  text: string;
  ariaLabel: string;
  onClick: () => void;
};

type LinkAction = {
  text: string;
  ariaLabel: string;
  href: string;
};

export type Action = ButtonAction | LinkAction;

const signLabel = "Sign documents";
const PleaseCallUs = () => <span aria-hidden="true">Please call us at:</span>;

function getCallHagertyAction(phone: string) {
  return {
    ariaLabel: `Call Hagerty at ${phone}`,
    href: `tel:+${phone}`,
    text: phone,
  };
}

export type Alert = {
  level: AlertLevel;

  /** Title, displayed in bold when there's a single alert */
  title: React.ReactNode;

  /** Optional message displayed after the title. Not bold. */
  message?: React.ReactNode;

  /** Optional button or link. Displays on the right. */
  action?: Action;
};

// Fetch alerts on load. Returns an array of Alerts to normalize the data structure for rendering alerts
export function useAlerts(sendESignDocument: UseESign["send"]): Alert[] {
  const urls = useUrls();
  const payBillAction = {
    ariaLabel: "Pay your bill",
    text: "Pay now",
    href: urls.billing.managePayments,
  };
  const logHasESignDocuments = useLogHasESignDocuments();

  const { data: alertsResponse } = useFetchApi<AlertsResponse>("alerts", 2);

  const {
    insuranceAutopayFailed = [],
    insuranceBillPastDue = [],
    hdcAutopayFailed,
    hdcBillPastDue,
    eSignDocuments = [],
  } = alertsResponse;

  logHasESignDocuments(eSignDocuments.length > 0);

  const alerts: Alert[] = [];

  if (hdcAutopayFailed) {
    alerts.push({
      title: (
        <>
          Payment for Drivers Club membership failed to process.{" "}
          <PleaseCallUs />
        </>
      ),
      action: getCallHagertyAction(phoneNumbers.hdc),
      level: "danger",
    });
  }

  if (hdcBillPastDue) {
    alerts.push({
      title: "Payment for Drivers Club membership is past due.",
      message: "Please make a payment.",
      action: payBillAction,
      level: "danger",
    });
  }

  insuranceAutopayFailed.forEach(({ country, policyNumber, state }) => {
    alerts.push({
      title: (
        <>
          Auto pay for {getStateName(state)} policy #{policyNumber} failed to
          process. <PleaseCallUs />
        </>
      ),
      action: getCallHagertyAction(phoneNumbers.insurancePaymentError(country)),
      level: "danger",
    });
  });

  insuranceBillPastDue.forEach(({ policyNumber, state }) => {
    alerts.push({
      title: `Bill for ${getStateName(
        state
      )} policy #${policyNumber} is past due.`,
      message: "Please make a payment.",
      action: payBillAction,
      level: "danger",
    });
  });

  eSignDocuments.forEach((eSignDocument) => {
    const stateName = getStateName(eSignDocument.state);
    alerts.push({
      title: "Signature required.",
      message: `Please sign the documents to finalize the ${stateName} policy change.`,
      action: {
        text: signLabel,
        ariaLabel: `E-Sign the ${stateName} policy change documents.`,
        onClick: () => sendESignDocument(eSignDocument),
      },
      level: "warning",
    });
  });

  return alerts;
}
